import { useRef, useState } from 'react';
import CyberLunaWindow from '../base/CyberLunaWindow';
import styles from './PerformancesShelf.module.css';
import content from '../../data/content.json';

const { performances } = content.gallery;

/**
 * PerformancesShelf — horizontally scrollable row of YouTube performance videos.
 *
 * Each video sits in its own XP window. Embeds use the facade pattern: only the
 * thumbnail (free from i.ytimg.com) renders until the visitor clicks play, so
 * the page never pays YouTube's ~1MB player cost up front. Clicking swaps in a
 * privacy-enhanced (youtube-nocookie) iframe with autoplay so one click plays.
 *
 * Videos live in content.json under gallery.performances.
 */
const PerformancesShelf = () => {
  const [playing, setPlaying] = useState(() => new Set());
  const trackRef = useRef(null);

  const play = (id) => {
    setPlaying((prev) => new Set(prev).add(id));
  };

  const scrollByCard = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.firstElementChild;
    const step = (card ? card.offsetWidth : 360) + 16;
    track.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  return (
    <section className={styles.shelf}>
      <h2 className={styles.title}>{performances.title}</h2>
      <p className={styles.subtitle}>{performances.subtitle}</p>

      <div className={styles.trackWrap}>
        <button
          type="button"
          className={`${styles.arrow} ${styles.arrowLeft}`}
          onClick={() => scrollByCard(-1)}
          aria-label="Scroll to previous video"
        >
          ‹
        </button>

        <div className={styles.track} ref={trackRef}>
          {performances.videos.map((video) => (
            <div key={video.id} className={styles.card}>
              <CyberLunaWindow
                isStatic
                title={video.title}
                bodyClassName={styles.cardBody}
              >
                {playing.has(video.id) ? (
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1`}
                    title={video.title}
                    className={styles.frame}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <button
                    type="button"
                    className={styles.facade}
                    onClick={() => play(video.id)}
                    aria-label={`Play: ${video.title}`}
                  >
                    <img
                      src={`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`}
                      alt=""
                      className={styles.thumb}
                      loading="lazy"
                    />
                    <span className={styles.playBadge} aria-hidden="true">▶</span>
                  </button>
                )}
              </CyberLunaWindow>
            </div>
          ))}
        </div>

        <button
          type="button"
          className={`${styles.arrow} ${styles.arrowRight}`}
          onClick={() => scrollByCard(1)}
          aria-label="Scroll to next video"
        >
          ›
        </button>
      </div>
    </section>
  );
};

export default PerformancesShelf;
